import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { Subscription, from, map, switchMap, take } from 'rxjs';
import { MASScheduleExtrasModel } from 'src/app/models/mas-schedules.model';
import { GoogleIdentityService, MAS_Accounts_Service, MAS_Programs_Service, MAS_Schedules_Service } from 'src/app/services';
import { AdvancedDebugging, DateTimeUtils } from 'src/app/utilities';
import { firebase } from 'src/environments/environment';

/**
 * Dashboard home: loads upcoming schedules from Firestore, enriches attendance for display,
 * and refreshes when the earliest session ends. Scoped to {@link DashboardComponent} via `providers`.
 */
@Injectable()
export class DashboardSchedulesFacade {
	private readonly advancedDebugging = new AdvancedDebugging();
	private readonly destroyRef = inject(DestroyRef);
	private readonly accountsService = inject(MAS_Accounts_Service);
	private readonly googleIdentityService = inject(GoogleIdentityService);
	private readonly programsService = inject(MAS_Programs_Service);
	private readonly schedulesService = inject(MAS_Schedules_Service);

	/** Snapshot list (updates on each Firestore emission for the active query). */
	readonly schedules = signal<MASScheduleExtrasModel[]>([]);
	readonly loading = signal(true);

	/** Live Firestore listener + enrichment chain; replaced on each refresh. */
	private liveSchedulesSub?: Subscription;
	private refreshTimer?: ReturnType<typeof setTimeout>;

	constructor() {
		this.destroyRef.onDestroy(() => {
			this.liveSchedulesSub?.unsubscribe();
			if (this.refreshTimer !== undefined) {
				clearTimeout(this.refreshTimer);
				this.refreshTimer = undefined;
			}
		});
	}

	/** Wait for auth, then attach the live schedule query (same as legacy `ngOnInit`). */
	start(): void {
		this.googleIdentityService.getUser.pipe(take(1)).subscribe({
			next: () => this.loadSchedulesPipeline(),
			error: err =>
				this.logError('dashboard:DashboardSchedulesFacade.start', err, {
					phase: 'auth',
				}),
		});
	}

	/**
	 * (Re)subscribe to the date-ranged schedule stream with fresh window parameters.
	 * Unsubscribes any previous Firestore listener so refreshes do not stack.
	 */
	private loadSchedulesPipeline(): void {
		this.liveSchedulesSub?.unsubscribe();
		this.liveSchedulesSub = from(this.buildScheduleQueryContext())
			.pipe(
				switchMap(({ startDate, endDate, reservePrograms }) =>
					this.schedulesService.getSchedulesByDateRangeAndLimit(startDate, endDate, 3).pipe(
						switchMap(schedules =>
							from(this.enrichSchedulesAsync(schedules, reservePrograms)).pipe(map(() => schedules)),
						),
					),
				),
			)
			.subscribe({
				next: schedules => {
					this.schedules.set(schedules);
					this.loading.set(false);
					this.queueAutoRefresh(schedules);
				},
				error: err => {
					this.loading.set(false);
					this.logError('dashboard:DashboardSchedulesFacade.loadSchedulesPipeline', err, {
						phase: 'schedules-stream',
					});
				},
			});
	}

	private async buildScheduleQueryContext(): Promise<{
		startDate: string;
		endDate: string;
		reservePrograms: string[];
	}> {
		const tzOffset = new Date().getTimezoneOffset() * 60000;
		const startDate = new Date(Date.now() - tzOffset).toISOString().slice(0, -1);
		const endDate = DateTimeUtils.formatDate(new Date(), 'midnight', firebase.offSetDays + 2);
		const reservePrograms = (await this.fetchProgramNames()).filter(name => name !== 'Team Sparring');
		reservePrograms.push('Blackbelt');
		return { startDate, endDate, reservePrograms };
	}

	private async enrichSchedulesAsync(
		schedules: MASScheduleExtrasModel[],
		reservePrograms: string[],
	): Promise<void> {
		await Promise.all(
			schedules.map(extrasModel =>
				extrasModel.enrichAttendance(
					this.accountsService,
					reservePrograms,
					(context, error) =>
						this.logError('dashboard:DashboardSchedulesFacade.enrichAttendance', error, {
							context,
							scheduleId: extrasModel.id,
						}),
					'name',
				),
			),
		);
	}

	private queueAutoRefresh(schedules: MASScheduleExtrasModel[]): void {
		if (this.refreshTimer !== undefined) {
			clearTimeout(this.refreshTimer);
			this.refreshTimer = undefined;
		}
		const endDateTime = schedules?.[0]?.schedule?.raw?.end?.dateTime;
		if (!endDateTime) {
			return;
		}
		const futureTime = new Date(endDateTime).getTime();
		const delay = futureTime - Date.now();
		if (delay > 0) {
			this.refreshTimer = setTimeout(() => this.loadSchedulesPipeline(), delay);
		}
	}

	private async fetchProgramNames(): Promise<string[]> {
		try {
			const programs = await this.programsService.getProgramsWithReservations();
			return programs.map(p => p.name);
		} catch (error) {
			this.logError('dashboard:DashboardSchedulesFacade.fetchProgramNames', error, {});
			return [];
		}
	}

	private logError(operation: string, error: unknown, extra: Record<string, unknown>): void {
		this.advancedDebugging.captureError(operation, error, {
			tags: { operation },
			app: 'dashboard',
			...extra,
		});
	}
}
